import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline';
import QRCodeRow from '../components/QRCodeRow';
import Pagination from '../components/Pagination';
import { useQRCodes } from '../hooks/useQRCodes';
import { CONFIG, authHeaders, authFetch } from '../utils/config';
import { useContent } from '../hooks/useContent';

const MyCodesPage = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const {
    qrCodes,
    loading,
    error,
    cachedStats,
    currentPage,
    totalPages,
    totalItems,
    updateQRCode,
    deleteQRCode,
    cacheStats,
    goToPage,
  } = useQRCodes();

  const { content } = useContent('myCodes');

  const handleUpdateQRCode = (updatedQRCode) => {
    updateQRCode(updatedQRCode);
  };

  const handleDeleteQRCode = async (qrCode) => {
    if (window.confirm(content?.confirmDelete || 'Supprimer ce QR Code ?')) {
      try {
        await deleteQRCode(qrCode.uuid);
      } catch {
        alert('Erreur lors de la suppression du QR Code');
      }
    }
  };

  const handleDownloadQRCode = async (qrCode) => {
    try {
      const response = await authFetch(`${CONFIG.BASE_URL}/qr-code/${qrCode.uuid}/download`, {
        headers: authHeaders(),
      });
      if (!response.ok) throw new Error(`Téléchargement impossible (${response.status}).`);

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const cd = response.headers.get('Content-Disposition');
      let filename = `QR-CODE-${qrCode.uuid}.png`;
      if (cd && cd.includes('filename=')) {
        filename = cd.split('filename=')[1].split(';')[0].replace(/"/g, '');
      }
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur téléchargement:', err);
      alert(content?.downloadError || 'Erreur de téléchargement');
    }
  };

  const handleStatsLoad = (uuid, stats) => {
    cacheStats(uuid, stats);
  };

  const filteredQRCodes = React.useMemo(() => {
    if (!Array.isArray(qrCodes)) return [];
    const searchLower = searchTerm.trim().toLowerCase();

    const filtered = qrCodes.filter((qrCode) => {
      if (!searchLower) return true;
      const url = (qrCode.redirect_url || qrCode.url || '').toLowerCase();
      const uuid = (qrCode.uuid || '').toLowerCase();
      const type = (qrCode.item_type || qrCode.type || '').toLowerCase();
      return url.includes(searchLower) || uuid.includes(searchLower) || type.includes(searchLower);
    });

    return filtered.sort((a, b) => {
      let aValue, bValue;
      switch (sortBy) {
        case 'created_at':
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case 'scan_count':
          aValue = cachedStats?.[a.uuid]?.total_visits || 0;
          bValue = cachedStats?.[b.uuid]?.total_visits || 0;
          break;
        case 'redirect_url':
          aValue = (a.redirect_url || a.url || '').toLowerCase();
          bValue = (b.redirect_url || b.url || '').toLowerCase();
          break;
        default:
          aValue = 0;
          bValue = 0;
      }
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      return 0;
    });
  }, [qrCodes, searchTerm, sortBy, sortOrder, cachedStats]);

  if (!content) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-dark-900' : 'bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? '' : 'bg-gray-50'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-8">

        <div className="mb-6 md:mb-8">
          <h1 className={`text-2xl md:text-3xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {content.pageTitle}
          </h1>
          <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>{content.pageSubtitle}</p>
        </div>

        <div className={`border rounded-xl p-4 md:p-6 mb-6 ${isDark ? 'bg-dark-800 border-dark-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlassIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              <input
                type="text"
                placeholder={content.searchPlaceholder}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark
                    ? 'bg-dark-700 border-dark-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
              />
            </div>
            <div className="flex items-center space-x-2">
              <FunnelIcon className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [newSortBy, newSortOrder] = e.target.value.split('-');
                  setSortBy(newSortBy);
                  setSortOrder(newSortOrder);
                }}
                className={`border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isDark
                    ? 'bg-dark-700 border-dark-600 text-white'
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="created_at-desc">{content.sortOptions.newest}</option>
                <option value="created_at-asc">{content.sortOptions.oldest}</option>
                <option value="scan_count-desc">{content.sortOptions.mostScanned}</option>
                <option value="scan_count-asc">{content.sortOptions.leastScanned}</option>
                <option value="redirect_url-asc">{content.sortOptions.urlAZ}</option>
                <option value="redirect_url-desc">{content.sortOptions.urlZA}</option>
              </select>
            </div>
          </div>
        </div>

        <div className={`rounded-xl border ${isDark ? 'bg-dark-800 border-dark-700' : 'bg-white border-gray-200 shadow-sm'}`}>
          <div className={`p-4 md:p-6 border-b ${isDark ? 'border-dark-700' : 'border-gray-200'}`}>
            <h2 className={`text-lg md:text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {content.listTitle}
            </h2>
          </div>

          <div className="p-4 md:p-6" id="qr-code-list">
            {loading ? (
              <div className="text-center py-12">
                <div className={`inline-flex items-center space-x-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
                  <span>{content.loading}</span>
                </div>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDark ? 'bg-red-900/20' : 'bg-red-50'}`}>
                  <svg className={`w-8 h-8 ${isDark ? 'text-red-400' : 'text-red-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{content.errorTitle}</h3>
                <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
                >
                  {content.retry}
                </button>
              </div>
            ) : totalItems === 0 ? (
              <div className="text-center py-12">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDark ? 'bg-dark-700' : 'bg-gray-100'}`}>
                  <svg className={`w-8 h-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                  </svg>
                </div>
                <h3 className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{content.empty}</h3>
                <p className={`mb-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{content.emptySubtitle}</p>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200"
                >
                  {content.emptyCTA}
                </button>
              </div>
            ) : filteredQRCodes.length === 0 ? (
              <div className="text-center py-12">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDark ? 'bg-dark-700' : 'bg-gray-100'}`}>
                  <svg className={`w-8 h-8 ${isDark ? 'text-yellow-400' : 'text-yellow-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className={`text-lg font-medium mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>{content.noResults}</h3>
                <p className={`mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{content.noResultsMessage}</p>
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="text-sm text-primary-400 hover:text-primary-300 underline"
                  >
                    {content.resetSearch}
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {filteredQRCodes.map((qrCode) => (
                  <QRCodeRow
                    key={qrCode.uuid}
                    qrCode={qrCode}
                    onStatsLoad={handleStatsLoad}
                    cachedStats={cachedStats[qrCode.uuid]}
                    showEditButton={true}
                    expandMode="edit"
                    onUpdate={handleUpdateQRCode}
                    onDownload={handleDownloadQRCode}
                    onDelete={handleDeleteQRCode}
                  />
                ))}
              </div>
            )}
          </div>

          {!loading && !error && Array.isArray(qrCodes) && qrCodes.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              currentItemsCount={filteredQRCodes.length}
              itemsPerPage={20}
              onPageChange={goToPage}
              loading={loading}
            />
          )}
        </div>

      </div>
    </div>
  );
};

export default MyCodesPage;
